import { makeDoubleDataField, makeBooleanDataField, makeStringDataField } from '@microsoft/oteljs';
var DEFAULT_SAMPLE_RATE = 0.02;
// An internal value used to set the precision of the sampling value
// 1000 matches with Microsoft Teams cohort size. Office client uses 10,000 buckets.
var SAMPLE_DENOMINATOR = 1000;
/**
 * Hashes a string value to a numeric value using djb2 hashing algorithm.
 * See https://en.wikipedia.org/wiki/Universal_hashing#Hashing_strings AND http://www.cse.yorku.ca/~oz/hash.html for algorithm details.
 * @param {string} str String value to be hashed
 * @return {number} - A positive numeric value
 */
function getDjb2Hash(str) {
    var hash = 5381;
    for (var i = 0; i < str.length; i++) {
        hash = (hash << 5) + hash + str.charCodeAt(i);
    }
    return hash >>> 0; // Ensure positive number
}
/**
 * Returns a sampling value in [0, 1) from an identifier, using djb2
 * @param identifier - An identifier to be hashed. If a GUID, should be lowercase with dashes only.
 */
function computeSamplingValue(identifier) {
    var hash = getDjb2Hash(identifier);
    return (hash % SAMPLE_DENOMINATOR) / SAMPLE_DENOMINATOR;
}
/**
 * A class that implements sampling. After creating the sampler,
 *  o Enable it by calling enableWithIdentifier, enableWithSamplingValue, or enableWithRandomValue()
 *  o Call setSamplingKey to indicate what type of identifier was used
 *  o Add the sampler as a preprocessor to the 1DS sink.
 * Only events that have SamplingPolicy explicitly set to Measure are sampled.
 * Sampling should generally be done on a session basis; events are marked with Session.SamplingKValue.
 */
var Sampler = /** @class */ (function () {
    function Sampler() {
        /**
         * The name of the preprocessor
         */
        this.name = 'Sampler';
        this.sampleRate = DEFAULT_SAMPLE_RATE;
    }
    /**
     * Enables sampling using an identifier as a seed for a djb2 hash function.
     * Returns whether sampling was successfully enabled (not whether the session is going to be sampled)
     * @param samplingKey - The name of the identifier that will be hashed. 'ClientId' and 'UserId' are canonical values
     * @param identifier - An identifier that will be hashed to determine whether to sample events
     *   If it is a GUID, it should be in lowercase with only dashes.
     */
    Sampler.prototype.enableWithIdentifier = function (samplingKey, identifier) {
        // Insufficient entropy if not long enough identifier
        if (typeof identifier !== 'string' || identifier.length < 4) {
            return false;
        }
        this.samplingValue = computeSamplingValue(identifier);
        this.samplingKey = samplingKey;
        return true;
    };
    /**
     * Enables sampling using the given sampling value
     * @param samplingKey - The name of the source of the sampling value. 'ClientId' and 'UserId' are canonical values
     * @param samplingValue - A value in [0, 1). If you do not want sampling, then do not add a Sampler.
     */
    Sampler.prototype.enableWithSamplingValue = function (samplingKey, samplingValue) {
        if (isNaN(samplingValue) || samplingValue < 0 || samplingValue >= 1) {
            return false;
        }
        this.samplingValue = samplingValue;
        this.samplingKey = samplingKey;
        return true;
    };
    /**
     * Enables sampling using a random sampling value
     */
    Sampler.prototype.enableWithRandomValue = function () {
        var byteArray = new Uint32Array(1);
        if (typeof window === 'object' && window.crypto) {
            window.crypto.getRandomValues(byteArray);
        }
        else {
            return false;
        }
        this.samplingValue = (byteArray[0] % SAMPLE_DENOMINATOR) / SAMPLE_DENOMINATOR;
        this.samplingKey = 'Random';
        return true;
    };
    /**
     * Overrides the default sample rate for Measure events.
     * @param sampleRate - The sample rate to set, between 0 and 1 inclusive.
     */
    Sampler.prototype.setSampleRate = function (sampleRate) {
        // Checking like this ensures that sampleRate is numeric
        if (sampleRate >= 0 && sampleRate <= 1) {
            this.sampleRate = sampleRate;
            return true;
        }
        return false;
    };
    /**
     * Gets the sampling value used by this sampler
     */
    Sampler.prototype.getSamplingValue = function () {
        return this.samplingValue;
    };
    /**
     * Returns whether this sampler allows sending measures
     */
    Sampler.prototype.isMeasuresEnabled = function () {
        return this.samplingValue === undefined || this.samplingValue < this.sampleRate;
    };
    /**
     * Possibly samples an event. Returns whether the event should be sent.
     * If sampling is enabled, adds field to the event
     * @param telemetryEvent - The event
     */
    Sampler.prototype.processEvent = function (telemetryEvent) {
        var samplingPolicy = telemetryEvent.eventFlags.samplingPolicy;
        // If the sampler hasn't been configured, don't do anything.
        // Also don't do anything if sampling policy isn't set.
        if (this.samplingValue === undefined || samplingPolicy === undefined) {
            return true;
        }
        // Measured events are sent if enabled
        // Diagnostics events are not sent
        // Otherwise, fall through and send at 100%
        if (samplingPolicy === 1 /* SamplingPolicy.Measure */) {
            if (this.isMeasuresEnabled()) {
                this.addSampleRateField(telemetryEvent, this.sampleRate, this.samplingValue);
                return true;
            }
            return false;
        }
        // else if, but the 'else' is unnecessary because the previous block returns
        if (samplingPolicy === 2 /* SamplingPolicy.Diagnostics */) {
            return false;
        }
        this.addSampleRateField(telemetryEvent, 1, this.samplingValue);
        return true;
    };
    Sampler.prototype.addSampleRateField = function (telemetryEvent, reportedSampleRate, samplingValue) {
        // Adding fields to Event Contract fields, which are sent without "Data.""
        if (!telemetryEvent.eventContract) {
            telemetryEvent.eventContract = { name: '', dataFields: [] };
        }
        var dataFields = telemetryEvent.eventContract.dataFields;
        dataFields.push(makeDoubleDataField('Event.SampleRate', reportedSampleRate));
        dataFields.push(makeDoubleDataField('Session.SamplingValue', samplingValue));
        if (this.samplingKey) {
            dataFields.push(makeStringDataField('Session.SamplingKey', this.samplingKey));
        }
        dataFields.push(makeBooleanDataField('Session.MeasuresEnabled', this.isMeasuresEnabled()));
    };
    return Sampler;
}());
export { Sampler };
//# sourceMappingURL=Sampler.js.map